import { observer } from 'mobx-react';
import useCases from '@/hooks/useCases';
import { MDXEditorMethods } from '@mdxeditor/editor';
import EditorComponent from './EditorComponent';
import AudioControls from './AudioControls';
import { useEffect, useRef, useState, useCallback } from 'react';
import { containerStyle, editorContainerStyle } from './CaseInputv2Styles';
import { useStreamedSpeechRec } from './useStreamedSpeechRec';
import AudioPlayer from './AudioPlayer';
import { fixDictation } from './DictationUtils';
import { fixMarkdownText } from '@/utils/transcriptReplacements';
import { fixNoteWithLLM } from '@/services/BackendAPI';
import useAlerts from '@/hooks/useAlerts';
import { Alert, Button } from '@mui/material';

interface CaseInputv2Props {
  showNoAudioBanner?: boolean;
}

export const CaseInputv2 = observer(({ showNoAudioBanner = false }: CaseInputv2Props) => {
  const cases = useCases();
  const alerts = useAlerts();
  const [isLoading, setIsLoading] = useState(false);
  const textboxRef = useRef<MDXEditorMethods>(null);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isNoAudioBannerDismissed, setIsNoAudioBannerDismissed] = useState(false);

  const lastUpdateTime = useRef<number>(performance.now());

  const handleEditorChanges = useCallback(
    (markdown: string) => {
      // Get the current time
      const currentTime = performance.now();
      // If more than 10 seconds have passed since the last update, update the note
      if (currentTime - lastUpdateTime.current > 15000) {
        cases.updateCaseNoteSilently(markdown);
        lastUpdateTime.current = currentTime; // Update the last updated time
      } else {
        cases.updateCaseNoteSilently(markdown, true);
      }
    },
    [cases],
  );

  const addDictation = useCallback((dictation: { text: string; isFinal: boolean }) => {
    const { text, isFinal } = dictation;
    if (isFinal) {
      const textFixed = fixDictation(text);
      insertIntoEditor(textFixed);
      cases.appendDictationToRawASROutput(textFixed);
      setInterimTranscript('');
    } else {
      setInterimTranscript(text);
    }
  }, []);

  const handleInsertTemplate = useCallback((templateText: string) => {
    templateText = fixMarkdownText(templateText);
    const templateAdmonition = `:::note\n${templateText}\n:::\n\n`;
    insertIntoEditor(templateAdmonition);
  }, []);

  const insertIntoEditor = useCallback((text: string) => {
    const textArea = textboxRef.current;

    if (!textArea) return;
    textArea.focus();
    if (textArea.getMarkdown().trim() === '') {
      textArea.setMarkdown(text);
    } else {
      textArea.insertMarkdown(text);
    }
  }, []);

  const { toggleRecording, stopRecording, recordingTime, state, audioBlob, dictationError } =
    useStreamedSpeechRec(addDictation);

  useEffect(() => {
    if (audioBlob) {
      cases.uploadFinalAudioToBackend(audioBlob);
    }
  }, [audioBlob, cases]);

  useEffect(() => {
    if (dictationError) {
      alerts.error(dictationError);
    }
  }, [dictationError, alerts]);

  useEffect(() => {
    setIsNoAudioBannerDismissed(false);
  }, [cases.caseId, showNoAudioBanner]);

  const handleFinalizeRecording = useCallback(async () => {
    const textArea = textboxRef.current;

    if (isLoading || !textArea) {
      return;
    }
    setIsLoading(true);

    stopRecording();

    let updatedText = cases.noteStructured.note;

    try {
      updatedText = await fixNoteWithLLM(cases.noteStructured.note, cases.caseId);
      textArea.focus();
      textArea.setMarkdown(updatedText);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      if (message !== 'Notat er for kort') {
        console.error('Error fixing note with LLM:', e);
      }
    }
    // Save case data
    try {
      await cases.updateCaseAndGenerateCodes(updatedText, cases.noteStructured.note);
    } catch (e) {
      console.error('Error updating case and generating codes:', e);
    }
    cases.enableCodesEditMode();
    setIsLoading(false);
  }, [isLoading, stopRecording, cases]);

  const showFinalizeButton =
    !isLoading &&
    (state === 'paused' || (state === 'idle' && textboxRef.current?.getMarkdown().length > 20));

  return (
    <div style={{ ...containerStyle, cursor: isLoading ? 'progress' : 'auto' }}>
      <div style={editorContainerStyle}>
        <EditorComponent
          note={cases.noteStructured.note}
          handleEditorChanges={handleEditorChanges}
          handleInsertTemplate={handleInsertTemplate}
          readOnly={isLoading}
          ref={textboxRef}
        />
      </div>
      {showNoAudioBanner && !isNoAudioBannerDismissed ? (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => setIsNoAudioBannerDismissed(true)}>
              Vis optager
            </Button>
          }
        >
          Der er ingen lyd i denne sag.
        </Alert>
      ) : state !== 'stopped' ? (
        <AudioControls
          state={state}
          recordingTime={recordingTime}
          toggleRecording={() => toggleRecording(cases.audioRecordingDevice)}
          audioBlob={audioBlob}
          showFinalizeButton={showFinalizeButton}
          handleFinalizeRecording={handleFinalizeRecording}
        />
      ) : audioBlob ? (
        <AudioPlayer src={URL.createObjectURL(audioBlob)} />
      ) : null}
      {interimTranscript && <div id="interimTranscript">{interimTranscript}</div>}
    </div>
  );
});
